
import React, { useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Alert,
  Button,
} from "@mui/material";
import {
  InventoryOutlined,
  SearchOutlined,
  AssignmentTurnedInOutlined,
  SupportAgentOutlined,
  NotificationsActiveOutlined,
  CheckCircleOutline,
  HelpOutlineOutlined,
  InfoOutlined,
  ArrowBackRounded,
} from "@mui/icons-material";

const steps = [
  {
    number: "۱",
    title: "انتخاب محصول",
    description: "محصول موردنظر خود را برای خرید عمده انتخاب کنید.",
    icon: SearchOutlined,
  },
  {
    number: "۲",
    title: "ثبت درخواست استعلام",
    description: "درخواست بررسی موجودی و قیمت را ثبت کنید.",
    icon: AssignmentTurnedInOutlined,
  },
  {
    number: "۳",
    title: "بررسی کارشناسان",
    description: "موجودی، قیمت نهایی و شرایط سفارش بررسی می‌شود.",
    icon: SupportAgentOutlined,
  },
  {
    number: "۴",
    title: "دریافت نتیجه",
    description: "نتیجه استعلام و شرایط خرید به شما اطلاع داده می‌شود.",
    icon: NotificationsActiveOutlined,
  },
  {
    number: "۵",
    title: "تأیید و ارسال",
    description: "پس از تأیید شما، سفارش برای ارسال نهایی می‌شود.",
    icon: CheckCircleOutline,
  },
];

export default function BulkPurchaseInfo({
  onRequestInquiry,
  onNotifyRestock,
}) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2.5, sm: 4, md: 6 },
        direction: "rtl",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: 3, md: 4 },
          border: "1px solid",
          borderColor: "grey.200",
          bgcolor: "#fff",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: { xs: 2.5, sm: 4, md: 5 },
            background:
              "linear-gradient(135deg, #fff5f6 0%, #ffffff 75%)",
            borderBottom: "1px solid",
            borderColor: "grey.100",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box sx={{ maxWidth: 680 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    bgcolor: "#fdecef",
                    color: "#ef4056",
                  }}
                >
                  <InventoryOutlined fontSize="medium" />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    sx={{
                      display: "block",
                      color: "#ef4056",
                      mb: 0.25,
                    }}
                  >
                    راهنمای خرید عمده
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color="text.primary"
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.55rem" },
                      lineHeight: 1.6,
                    }}
                  >
                    خرید عمده با استعلام قیمت و موجودی
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 2,
                  fontSize: { xs: "0.9rem", sm: "0.98rem" },
                }}
              >
                محصول موردنظر خود را انتخاب کنید و درخواست استعلام دهید.
                کارشناسان ما موجودی، قیمت نهایی و شرایط سفارش را بررسی
                می‌کنند تا پیش از خرید، اطلاعات لازم را در اختیار داشته باشید.
              </Typography>
            </Box>

            {onRequestInquiry && (
              <Button
                variant="contained"
                fullWidth
                onClick={onRequestInquiry}
                endIcon={
                  <ArrowBackRounded
                    sx={{ transform: "rotate(180deg)" }}
                  />
                }
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", md: "center" },
                  width: { xs: "100%", md: "auto" },
                  minHeight: 48,
                  px: 2.5,
                  borderRadius: 2.5,
                  bgcolor: "#ef4056",
                  color: "#fff",
                  fontWeight: 800,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#d9364a",
                    boxShadow: "0 5px 14px rgba(239,64,86,0.16)",
                  },
                }}
              >
                درخواست استعلام عمده
              </Button>
            )}
          </Box>
        </Box>

        {/* PURCHASE PROCESS */}
        <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              color="text.primary"
              sx={{ fontSize: { xs: "1.05rem", sm: "1.2rem" } }}
            >
              مراحل خرید
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, lineHeight: 1.8 }}
            >
              از ثبت درخواست تا تأیید نهایی سفارش
            </Typography>
          </Box>

          <Box
            component="ol"
            aria-label="مراحل خرید عمده"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
              gap: 2,
              p: 0,
              m: 0,
              listStyle: "none",
            }}
          >
            {steps.map((step) => {
              const StepIcon = step.icon;

              return (
                <Paper
                  component="li"
                  key={step.number}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    p: 2.25,
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 3,
                    bgcolor: "#fff",
                    transition: "border-color 0.2s, background-color 0.2s",
                    "&:hover": {
                      borderColor: "#f3b2bc",
                      bgcolor: "#fffafb",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#fff0f2",
                        color: "#ef4056",
                      }}
                    >
                      <StepIcon sx={{ fontSize: 24 }} />
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: "grey.100",
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 800,
                      }}
                    >
                      {step.number}
                    </Box>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    color="text.primary"
                    sx={{ mb: 1, lineHeight: 1.8 }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.9,
                      fontSize: "0.82rem",
                    }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* RESTOCK NOTIFICATION */}
          <Paper
            elevation={0}
            sx={{
              mt: 4,
              p: { xs: 2, sm: 2.75 },
              border: "1px solid",
              borderColor: "#d7e8f8",
              borderRadius: 3,
              bgcolor: "#f5faff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "#e2f1ff",
                    color: "#1976b8",
                  }}
                >
                  <HelpOutlineOutlined />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    محصول ناموجود است؟
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.9 }}
                  >
                    درخواست اطلاع‌رسانی ثبت کنید تا در صورت موجود شدن
                    محصول، از وضعیت آن مطلع شوید.
                  </Typography>
                </Box>
              </Box>

              {onNotifyRestock && (
                <Button
                  variant="outlined"
                  onClick={onNotifyRestock}
                  startIcon={<NotificationsActiveOutlined />}
                  sx={{
                    flexShrink: 0,
                    minHeight: 44,
                    px: 2.5,
                    borderRadius: 2.5,
                    borderColor: "#8abfe5",
                    color: "#176da5",
                    fontWeight: 800,
                    "&:hover": {
                      borderColor: "#176da5",
                      bgcolor: "#eaf5ff",
                    },
                  }}
                >
                  خبرم کن
                </Button>
              )}
            </Box>
          </Paper>

          {/* IMPORTANT NOTICE */}
          <Alert
            severity="warning"
            icon={<InfoOutlined fontSize="small" />}
            sx={{
              mt: 2,
              borderRadius: 2.5,
              alignItems: "center",
              bgcolor: "#fffaf0",
              border: "1px solid #f2e2bd",
              color: "#805518",
              "& .MuiAlert-icon": {
                color: "#b7791f",
              },
              "& .MuiAlert-message": {
                fontSize: "0.84rem",
                lineHeight: 1.9,
              },
            }}
          >
            <Box component="span" fontWeight={800}>
              توجه:{" "}
            </Box>
            ثبت درخواست استعلام به‌معنای تأیید موجودی یا ثبت قطعی سفارش نیست.
            نهایی شدن سفارش پس از بررسی و تأیید شرایط خرید انجام می‌شود.
          </Alert>
        </Box>
      </Paper>
    </Container>
  );
}
